/**
 * Вкладка «Сценарий» видео-проекта (`/project/:id?tab=scenario`, спека §9.2, §5.3–5.4; v2 §1.6).
 *
 * Сверху — версии v1…vN (по умолчанию открыта применённая; выбранная — в адресе `?v=`): содержимое
 * версии с `_fixes` жёлтым — детали платья, «лицо» у кадров (у версий v1 — без деталей и без лица;
 * титра и звука у сценария нет, в старых версиях они не показываются), «Применить к кадрам» (409 `SHOTS_HAVE_GENERATIONS` → «создать копию и
 * применить там»), «Применить только поля ролика», «Править → сохранить как версию» (форма v2; версия
 * v1 переводится `toV2`).
 *
 * Ниже — новая версия от композера; когда версии есть, форма свёрнута в «Новая версия…». Бриф, модели (они задают потолок длительности кадра), шесть
 * референсов стилевой библиотеки (автоподбор как на сервере — works первыми; «выбрать другие» —
 * окно референсов на библиотеке проекта) и «Сгенерировать сценарий ≈ $0.05» — ПЛАТНО: замок на время
 * запроса, ключ идемпотентности живёт до успешного ответа (`useCompose`).
 */

import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { ChevronDown, Copy, ImageOff, Pencil, Sparkles, X } from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { RefPickerDialog } from "@/components/fitting/RefPickerDialog";
import { ApiFailure } from "@/lib/api";
import { Btn, Empty, ErrorLine, Hint, Panel } from "@/components/project/ui";
import { Dialog, DialogFooter } from "@/components/trends/Dialog";
import { Chip } from "@/components/trends/ui";
import { libraryKeys, loadLibrary, type ReferenceItem } from "@/lib/library";
import { mediaUrl, when } from "@/lib/media";
import { errorText, usd } from "@/lib/project";
import { loadReferenceCards, referenceKeys } from "@/lib/references";
import {
  cloneLink,
  FRAMING_LABEL,
  SHOT_TRANSITION_LABEL,
  useApplyScenario,
  useCloneAndApply,
  useCompose,
  useScenarios,
  useVideoOptions,
  type ApplyMode,
  type ScenarioJson,
  type ScenarioVersion,
  type VideoView,
} from "@/lib/video";
import {
  autoRefIds,
  COMPOSER_REFS,
  firstRunCodes,
  fixMarks,
  maxShotSeconds,
  scenarioSumS,
  toV2,
} from "@/lib/videoTimeline";

import { Sec } from "./bits";
import { blankScenario, ScenarioEditor, type ScenarioDraft } from "./ScenarioEditor";

/** Ориентир цены вызова композера (§5.1: ≈ $0.03–0.06). */
const COMPOSE_PRICE = "≈ $0.05";

export function ScenarioPanel({ pid, video }: { pid: number; video: VideoView }) {
  const list = useScenarios(pid);
  const versions = list.data?.versions ?? video.scenario.versions;
  const appliedId = list.data ? list.data.applied_id : (video.scenario.applied?.id ?? null);
  const [search, setSearch] = useSearchParams();
  const asked = Number(search.get("v"));
  const current =
    versions.find((v) => v.id === asked) ?? versions.find((v) => v.id === appliedId) ?? versions[0] ?? null;
  const [editing, setEditing] = useState<{ json: ScenarioDraft; basedOn: number | null; fromV1: boolean } | null>(
    null,
  );

  const select = (id: number) =>
    setSearch(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set("v", String(id));
        return params;
      },
      { replace: true },
    );

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-4">
      <Panel
        title="Версии сценария"
        aside={
          !editing ? (
            <button
              type="button"
              className="text-accent-text hover:underline"
              onClick={() => setEditing({ json: blankScenario(video.duration_ms), basedOn: null, fromV1: false })}
            >
              написать руками
            </button>
          ) : undefined
        }
        annot="версии сценария"
      >
        <ErrorLine text={list.isError ? errorText(list.error) : ""} className="mb-2" />
        {versions.length ? (
          <div className="mb-3 flex flex-wrap gap-1.5" role="tablist" aria-label="версии">
            {[...versions].reverse().map((v) => (
              <VersionChip key={v.id} v={v} on={v.id === current?.id} applied={v.id === appliedId} onPick={() => select(v.id)} />
            ))}
          </div>
        ) : null}

        {editing ? (
          <ScenarioEditor
            key={`${editing.basedOn ?? "new"}`}
            pid={pid}
            initial={editing.json}
            basedOn={editing.basedOn}
            fromV1={editing.fromV1}
            targetMs={video.duration_ms}
            onCancel={() => setEditing(null)}
            onSaved={(id) => {
              setEditing(null);
              select(id);
            }}
          />
        ) : current ? (
          <VersionView
            key={current.id}
            pid={pid}
            version={current}
            applied={current.id === appliedId}
            onEdit={() =>
              setEditing({ json: toV2(current.json), basedOn: current.version, fromV1: !current.json.dress_details?.length })
            }
          />
        ) : list.isPending && !versions.length ? (
          <Hint>загружаю…</Hint>
        ) : (
          <Empty>Сценария ещё нет — сгенерируйте или напишите руками.</Empty>
        )}
      </Panel>

      {/* Одно место в дереве (порядок — CSS): после первой версии форма не пересоздаётся и не теряет ответ. */}
      <div className={versions.length === 0 ? "order-first" : undefined}>
        <ComposePanel pid={pid} video={video} onComposed={select} startOpen={versions.length === 0} />
      </div>
    </div>
  );
}

function VersionChip({ v, on, applied, onPick }: { v: ScenarioVersion; on: boolean; applied: boolean; onPick: () => void }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={on}
      onClick={onPick}
      title={`${v.origin === "composer" ? "композер" : "вручную"} · ${v.author_name} · ${when(v.date_create)}${applied ? " · применена" : ""}`}
      className={clsx(
        "inline-flex h-7 items-center gap-1 rounded-[var(--radius-ui)] border px-2 text-[12px] tnum transition-colors",
        on ? "border-accent bg-accent-dim font-medium text-accent-text" : "border-border bg-bg text-fg-muted hover:text-fg",
        applied && "ring-1 ring-ok",
      )}
    >
      v{v.version}
      {v.origin === "manual" ? <Pencil size={10} /> : null}
      {applied ? <span className="text-[10.5px] text-ok">✓</span> : null}
    </button>
  );
}

// ---------------------------------------------------------------------------
// новая версия от композера (ПЛАТНО)
// ---------------------------------------------------------------------------

function ComposePanel({
  pid,
  video,
  onComposed,
  startOpen,
}: {
  pid: number;
  video: VideoView;
  onComposed: (id: number) => void;
  /** Версий нет — форма открыта сразу; есть — свёрнута в «Новая версия…». */
  startOpen: boolean;
}) {
  const [open, setOpen] = useState(startOpen);
  const compose = useCompose(pid);
  const options = useVideoOptions();
  const models = useMemo(() => options.data?.models ?? [], [options.data]);
  const [brief, setBrief] = useState("");
  /** `null` — модели первого прогона (так же считает сервер без выбора). */
  const [chosen, setChosen] = useState<string[] | null>(null);
  const picked = chosen ?? firstRunCodes(models).filter((c) => models.some((m) => m.code === c && m.discovered));
  const maxS = maxShotSeconds(models, chosen ?? []);

  const library = video.library;
  const items = useQuery({
    queryKey: libraryKeys.one(library?.id ?? 0),
    queryFn: () => loadLibrary(library?.id ?? 0),
    enabled: library !== null,
  });
  const auto = useMemo(() => autoRefIds(items.data?.items ?? []), [items.data]);
  const [custom, setCustom] = useState<number[] | null>(null);
  const [picking, setPicking] = useState(false);
  const refIds = custom ?? auto;

  // Миниатюры выбранных руками, которых нет в библиотеке проекта (окно позволяет сменить библиотеку).
  const inLibrary = useMemo(() => new Map((items.data?.items ?? []).map((i) => [i.id, i])), [items.data]);
  const foreign = (custom ?? []).filter((id) => !inLibrary.has(id));
  const cards = useQuery({
    queryKey: referenceKeys.cards(foreign),
    queryFn: () => loadReferenceCards(foreign),
    enabled: foreign.length > 0,
  });
  const itemOf = (id: number): ReferenceItem | undefined =>
    inLibrary.get(id) ?? cards.data?.items.find((i) => i.id === id);

  // Новая версия — сразу на экран справа.
  const done = compose.data;
  useEffect(() => {
    if (done) onComposed(done.scenario.id);
  }, [done]);

  const toggle = (code: string) => {
    const next = picked.includes(code) ? picked.filter((c) => c !== code) : [...picked, code];
    setChosen(next);
  };

  if (!open)
    return (
      <Panel annot="композер сценария">
        <div className="flex flex-wrap items-center gap-3">
          <Btn onClick={() => setOpen(true)}>
            <Sparkles size={13} />
            Новая версия…
            <ChevronDown size={13} />
          </Btn>
          <Hint>композер напишет новую версию по брифу и референсам — платно, {COMPOSE_PRICE}</Hint>
        </div>
      </Panel>
    );

  return (
    <Panel
      title="Новая версия"
      aside={
        !startOpen ? (
          <button type="button" className="text-accent-text hover:underline" onClick={() => setOpen(false)}>
            свернуть
          </button>
        ) : undefined
      }
      annot="композер сценария"
    >
      <div className="grid gap-3">
        <label className="grid gap-1">
          <span className="text-[11.5px] text-fg-faint">Бриф (необязательно)</span>
          <textarea
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            rows={4}
            maxLength={4000}
            placeholder="что важно в этом ролике: настроение, акцент платья, где снимаем"
            className="resize-y rounded-[var(--radius-ui)] border border-border bg-bg px-2.5 py-2 text-[12.5px] leading-[1.5] text-fg outline-none placeholder:text-fg-faint focus:border-accent"
          />
        </label>

        <div className="grid gap-1.5">
          <span className="text-[11.5px] text-fg-faint">
            Модели ролика — от них потолок кадра: <span className="tnum">2–{String(maxS).replace(".", ",")}</span> с
          </span>
          {options.isPending ? (
            <Hint>загружаю модели…</Hint>
          ) : options.isError ? (
            <ErrorLine text={errorText(options.error)} />
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {models.map((m) => {
                const off = !m.discovered;
                const top = m.durations.length ? Math.max(...m.durations) : null;
                return (
                  <span key={m.code} className={off ? "opacity-50" : undefined}>
                    <Chip
                      on={!off && picked.includes(m.code)}
                      title={off ? `нет discovery: reels-runner video discover` : `${m.code}${top ? ` · кадр до ${top} с` : ""}`}
                      onClick={() => {
                        if (!off) toggle(m.code);
                      }}
                    >
                      {m.label}
                      {top ? (
                        <span className="ml-1 text-fg-faint">
                          <span className="tnum">≤{top}</span> с
                        </span>
                      ) : null}
                    </Chip>
                  </span>
                );
              })}
            </div>
          )}
          {chosen !== null ? (
            <button type="button" className="justify-self-start text-[11.5px] text-accent-text hover:underline" onClick={() => setChosen(null)}>
              как в первом прогоне
            </button>
          ) : null}
        </div>

        <div className="grid gap-1.5">
          <div className="flex items-baseline gap-2">
            <span className="text-[11.5px] text-fg-faint">
              Референсы стиля {custom ? "— выбраны руками" : "— автоподбор"}
              {library ? ` из «${library.name}»` : ""}
            </span>
          </div>
          {library === null ? (
            <div className="text-[12px] text-warn">У проекта нет стилевой библиотеки — композер не запустится.</div>
          ) : items.isPending ? (
            <Hint>загружаю библиотеку…</Hint>
          ) : items.isError ? (
            <ErrorLine text={errorText(items.error)} />
          ) : refIds.length === 0 ? (
            <Empty>В библиотеке нет референсов.</Empty>
          ) : (
            <div className="grid max-w-[480px] grid-cols-6 gap-1">
              {refIds.map((id) => {
                const item = itemOf(id);
                return (
                  <div key={id} className="group relative" title={item?.note ?? `#${id}`}>
                    {item?.asset_path ? (
                      <img
                        src={mediaUrl(item.asset_path)}
                        alt=""
                        loading="lazy"
                        className={clsx(
                          "aspect-[3/4] w-full rounded-[3px] border object-cover",
                          item.verdict === "works" ? "border-ok" : "border-border",
                        )}
                      />
                    ) : (
                      <div className="grid aspect-[3/4] w-full place-items-center rounded-[3px] border border-dashed border-border-strong text-fg-faint">
                        <ImageOff size={12} />
                      </div>
                    )}
                    {custom ? (
                      <button
                        type="button"
                        aria-label="убрать референс"
                        onClick={() => setCustom(custom.filter((x) => x !== id))}
                        className="absolute top-0.5 right-0.5 hidden size-4 place-items-center rounded-[3px] bg-black/60 text-white group-hover:grid"
                      >
                        <X size={10} />
                      </button>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
          {library !== null ? (
            <div className="flex flex-wrap gap-3 text-[11.5px]">
              <button type="button" className="text-accent-text hover:underline" onClick={() => setPicking(true)}>
                выбрать другие
              </button>
              {custom ? (
                <button type="button" className="text-accent-text hover:underline" onClick={() => setCustom(null)}>
                  вернуть автоподбор
                </button>
              ) : null}
              <span className="text-fg-faint">до {COMPOSER_REFS}; рамка — вердикт «работает»</span>
            </div>
          ) : null}
        </div>

        <div className="grid gap-1.5 border-t border-border pt-3">
          <Btn
            tone="primary"
            disabled={compose.busy || library === null}
            onClick={() => compose.run({ brief: brief.trim(), models: chosen ?? [], ref_ids: custom ?? [] })}
            title="ПЛАТНО: один вызов композера (OpenRouter)"
          >
            <Sparkles size={13} />
            {compose.busy ? "Композер пишет сценарий…" : `Сгенерировать сценарий ${COMPOSE_PRICE}`}
          </Btn>
          <Hint>
            Платно: фото платья и референсы уходят композеру, ответ — новая версия. До ответа — до минуты; повторный
            клик до ответа денег не тратит.
          </Hint>
          {done ? (
            <div className="text-[12px] text-ok">
              {done.existing ? "Версия уже была — " : "Готово — "}v{done.scenario.version}
              {done.existing ? "" : ` · ${usd(done.cost)}`}
            </div>
          ) : null}
          <ErrorLine text={compose.error} />
          {compose.failure instanceof ApiFailure && compose.failure.code === "BAD_SCENARIO" ? (
            <Hint>Вызов композера оплачен, ответ сохранён в журнале «/calls». Ещё раз — новый платный вызов.</Hint>
          ) : null}
        </div>
      </div>

      <RefPickerDialog
        open={picking}
        onClose={() => setPicking(false)}
        title="Референсы для композера"
        libraryId={library?.id}
        takenPins={[]}
        takenRefs={[]}
        onAdd={(_pins, refs) => {
          if (refs.length) setCustom(refs.slice(0, COMPOSER_REFS));
        }}
      />
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// версия: содержимое и применение
// ---------------------------------------------------------------------------

const FIX_TEXT = (fix: string): string => {
  const shot = /^(invariant|heroine): shot (\d+)$/.exec(fix);
  if (shot)
    return shot[1] === "invariant"
      ? `кадр ${shot[2]}: инвариант в конце видео-промпта`
      : `кадр ${shot[2]}: героиня и свет в начале промпта стилла`;
  if (fix.startsWith("negative")) return "негатив: обязательный хвост (правило 7)";
  const face = /^face: shot (\d+)$/.exec(fix);
  if (face) return `кадр ${face[1]}: финал со спины/деталью — без лица`;
  return fix;
};

function VersionView({
  pid,
  version,
  applied,
  onEdit,
}: {
  pid: number;
  version: ScenarioVersion;
  applied: boolean;
  onEdit: () => void;
}) {
  const apply = useApplyScenario(pid);
  const [armed, setArmed] = useState(false);
  const [cloneOpen, setCloneOpen] = useState(false);
  const json = version.json;
  const marks = fixMarks(json._fixes);
  const totalS = scenarioSumS(json.shots);

  const run = (mode: ApplyMode) => {
    if (apply.isPending) return;
    setArmed(false);
    apply.mutate(
      { scenarioId: version.id, mode },
      {
        onError: (e) => {
          if (cloneLink(e) !== null) setCloneOpen(true);
        },
      },
    );
  };
  const conflict = apply.isError && cloneLink(apply.error) !== null;
  const result = apply.data?.applied;

  return (
    <div className="grid gap-3" data-annot={`версия сценария ${version.version}`}>
      <div className="text-[12px] text-fg-muted">
        <b className="text-fg">v{version.version}</b> · {version.origin === "composer" ? "композер" : "вручную"} ·{" "}
        {version.author_name} · {when(version.date_create)}
        {version.model ? ` · ${version.model}` : ""}
        {version.cost !== null ? ` · ${usd(version.cost)}` : ""}
        {applied ? (
          <span className="ml-1.5 rounded-full bg-ok-bg px-2 py-0.5 text-[11px] text-ok">
            применена{version.date_apply ? ` ${when(version.date_apply)}` : ""}
          </span>
        ) : null}
      </div>
      {version.brief ? <Hint>Бриф: {version.brief}</Hint> : null}

      {json._fixes.length ? (
        <div className="rounded-[var(--radius-ui)] border border-warn/40 bg-warn-bg px-2.5 py-1.5 text-[12px] text-warn">
          Сервер дописал за композера: {json._fixes.map(FIX_TEXT).join("; ")}.
        </div>
      ) : null}

      <dl className="grid grid-cols-[110px_minmax(0,1fr)] gap-x-3 gap-y-1 text-[12.5px]">
        <Row label="Логлайн">{json.logline}</Row>
        <Row label="Хук">{json.hook}</Row>
        <Row label="Детали платья">
          {json.dress_details?.length ? (
            <span className="font-mono text-[11.5px]">{json.dress_details.join(" · ")}</span>
          ) : (
            <span className="text-fg-faint">нет — версия v1</span>
          )}
        </Row>
        <Row label="Негатив" fixed={marks.negative}>
          <span className="font-mono text-[11.5px]">{json.negative}</span>
        </Row>
        <Row label="Героиня">{json.heroine}</Row>
        <Row label="Свет">{json.light}</Row>
      </dl>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left text-[12px]">
          <thead className="text-[11px] text-fg-faint">
            <tr className="border-b border-border">
              <th className="px-1.5 py-1 font-normal">№</th>
              <th className="px-1.5 py-1 font-normal">кадр · действие · акцент платья</th>
              <th className="px-1.5 py-1 font-normal">длит.</th>
              <th className="px-1.5 py-1 font-normal">крупность</th>
              <th className="px-1.5 py-1 font-normal">камера</th>
              <th className="px-1.5 py-1 font-normal" title="лицо героини в кадре: модель, отклоняющая лица, отдаст кадр запасной">
                лицо
              </th>
              <th className="px-1.5 py-1 font-normal">переход</th>
            </tr>
          </thead>
          <tbody>
            {json.shots.map((s, i) => {
              const n = i + 1;
              return (
                <ShotRows
                  key={n}
                  n={n}
                  shot={s}
                  heroineFixed={marks.heroine.has(n)}
                  invariantFixed={marks.invariant.has(n)}
                />
              );
            })}
          </tbody>
        </table>
      </div>
      <Hint>
        Кадров {json.shots.length} · сумма <Sec ms={totalS * 1000} />
        {json.shots.slice(0, -1).some((s) => s.transition === "crossfade") ? " (наплыв −0,3 с)" : ""}. Жёлтым — что
        сервер дописал за композера.
      </Hint>

      <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
        {armed ? (
          <>
            <span className="text-[12px] text-warn">
              Заголовки, промпты, длительности, камера, переходы и «лицо в кадре» кадров перепишутся по v
              {version.version}.
            </span>
            <Btn tone="primary" disabled={apply.isPending} onClick={() => run("shots")}>
              Да, применить
            </Btn>
            <Btn tone="ghost" onClick={() => setArmed(false)}>
              Отмена
            </Btn>
          </>
        ) : (
          <>
            <Btn tone="primary" disabled={apply.isPending} onClick={() => setArmed(true)}>
              Применить к кадрам
            </Btn>
            <Btn
              disabled={apply.isPending}
              onClick={() => run("fields")}
              title="негатив, логлайн — кадры не трогаются"
            >
              Применить только поля ролика
            </Btn>
            <Btn tone="ghost" onClick={onEdit}>
              <Pencil size={13} />
              Править → сохранить как версию
            </Btn>
          </>
        )}
      </div>
      {apply.isPending ? <Hint>применяю…</Hint> : null}
      {result ? (
        <div className="text-[12px] text-ok">
          {result.mode === "fields"
            ? `Поля ролика v${version.version} действуют; кадры не тронуты.`
            : `Применено к кадрам: обновлено ${result.updated}, добавлено ${result.created}, удалено ${result.deleted}.`}{" "}
          {result.mode === "shots" ? (
            <Link to="?tab=shots" className="text-accent-text hover:underline">
              к кадрам →
            </Link>
          ) : null}
        </div>
      ) : null}
      <ErrorLine text={apply.isError && !conflict ? errorText(apply.error) : ""} />
      {conflict && !cloneOpen ? (
        <div className="text-[12px] text-warn">
          {errorText(apply.error)}{" "}
          <button type="button" className="text-accent-text hover:underline" onClick={() => setCloneOpen(true)}>
            создать копию проекта…
          </button>
        </div>
      ) : null}

      <CloneDialog pid={pid} open={cloneOpen} json={json} version={version.version} onClose={() => setCloneOpen(false)} />
    </div>
  );
}

/** Кадр версии: строка с полями и под ней — промпты стилла и видео (в две колонки, без широкой таблицы). */
function ShotRows({
  n,
  shot: s,
  heroineFixed,
  invariantFixed,
}: {
  n: number;
  shot: ScenarioJson["shots"][number];
  heroineFixed: boolean;
  invariantFixed: boolean;
}) {
  const face = typeof s.face_visible === "boolean" ? (s.face_visible ? "да" : "нет") : "—";
  return (
    <>
      <tr className="align-top">
        <td className="tnum px-1.5 pt-1.5 font-semibold text-fg-muted">{n}</td>
        <td className="px-1.5 pt-1.5">
          <div className="font-medium text-fg">{s.title}</div>
          <div className="text-fg-muted">{s.action}</div>
          <div className="text-fg-faint">акцент: {s.dress_focus}</div>
        </td>
        <td className="px-1.5 pt-1.5">
          <Sec ms={s.duration_s * 1000} />
        </td>
        <td className="px-1.5 pt-1.5">{FRAMING_LABEL[s.framing]}</td>
        <td className="px-1.5 pt-1.5">{s.camera}</td>
        <td
          className="px-1.5 pt-1.5"
          title={face === "—" ? "в версии v1 признака нет — при применении «лицо в кадре»" : undefined}
        >
          {face}
        </td>
        <td className="px-1.5 pt-1.5">{SHOT_TRANSITION_LABEL[s.transition]}</td>
      </tr>
      <tr className="border-b border-border last:border-b-0">
        <td />
        <td colSpan={6} className="px-1.5 pt-1 pb-2">
          <div className="grid gap-2 text-[11.5px] text-fg-muted md:grid-cols-2">
            <div className={clsx("rounded-[3px]", heroineFixed && "bg-warn-bg px-1")}>
              <span className="text-fg-faint">стилл: </span>
              {s.keyframe_prompt}
            </div>
            <div className={clsx("rounded-[3px]", invariantFixed && "bg-warn-bg px-1")}>
              <span className="text-fg-faint">видео: </span>
              {s.video_prompt}
            </div>
          </div>
        </td>
      </tr>
    </>
  );
}

function Row({ label, fixed = false, children }: { label: string; fixed?: boolean; children: ReactNode }) {
  return (
    <>
      <dt className="text-[11.5px] text-fg-faint">{label}</dt>
      <dd className={clsx("min-w-0 text-fg", fixed && "rounded-[3px] bg-warn-bg px-1")}>{children}</dd>
    </>
  );
}

/**
 * 409 `SHOTS_HAVE_GENERATIONS`: кадры с генерациями переписывать нельзя — копия проекта (платье,
 * библиотека, цель, слоты; без кадров и генераций), в ней та же версия и применение к кадрам.
 */
function CloneDialog({
  pid,
  open,
  json,
  version,
  onClose,
}: {
  pid: number;
  open: boolean;
  json: ScenarioJson;
  version: number;
  onClose: () => void;
}) {
  const clone = useCloneAndApply(pid);
  const navigate = useNavigate();
  const partial = clone.data && clone.data.error ? clone.data : null;

  return (
    <Dialog open={open} onClose={onClose} title="У кадров есть генерации">
      <div className="grid gap-2 px-4 py-3 text-[12.5px] text-fg">
        <p>
          У кадров есть генерации. Создать проект-копию и применить там? В копию переедут платье, стилевая
          библиотека, цель ролика и фото проекта; кадры с генерациями останутся здесь, а v{version} станет кадрами
          копии.
        </p>
        <ErrorLine text={clone.isError ? errorText(clone.error) : ""} />
        {partial ? (
          <div className="text-[12px] text-warn">
            Копия создана, но сценарий в ней не применился: {partial.error}{" "}
            <Link to={`/project/${partial.id}?tab=scenario`} className="text-accent-text hover:underline" onClick={onClose}>
              открыть копию →
            </Link>
          </div>
        ) : null}
      </div>
      <DialogFooter>
        <Btn tone="ghost" onClick={onClose} disabled={clone.isPending}>
          Отмена
        </Btn>
        <Btn
          tone="primary"
          disabled={clone.isPending || partial !== null}
          onClick={() =>
            clone.mutate(toV2(json), {
              onSuccess: ({ id, error }) => {
                if (error) return;
                onClose();
                navigate(`/project/${id}?tab=scenario`);
              },
            })
          }
        >
          <Copy size={13} />
          {clone.isPending ? "создаю копию…" : "Создать копию и применить"}
        </Btn>
      </DialogFooter>
    </Dialog>
  );
}
